import React, { useState, useEffect } from 'react';
import { usePxToVw } from '../hooks/usePxToVw';


const BubblesIntegration = ({ aiFormData, disableSpeechButton }) => {
  const { pxToVw } = usePxToVw();
  const [bubbles, setBubbles] = useState([]);



  useEffect(() => {
    injectBubbleAnimations();
  }, []);

  const hexToRgba = (hex, alpha = 1) => {
    hex = hex.replace('#', '');

    alpha = alpha / 100;

    // Handle short form (#RGB)
    if (hex.length === 3) {
      const r = parseInt(hex[0] + hex[0], 16);
      const g = parseInt(hex[1] + hex[1], 16);
      const b = parseInt(hex[2] + hex[2], 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }

    // Handle 6-digit (#RRGGBB)
    if (hex.length === 6) {
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }

    // Handle 8-digit (#RRGGBBAA)
    if (hex.length === 8) {
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      const a = parseInt(hex.substring(6, 8), 16) / 255;
      return `rgba(${r}, ${g}, ${b}, ${a})`;
    }

    throw new Error("Invalid hex color format");
  }


  // Convert shadow schema -> CSS shadow
  const getShadowCSS = bubble => {
    if ((bubble.custom === true) || bubble.method === 'custom') return 'none'; // Image bubble has NO shadow

    const angleRad = (bubble.shadowAngle * Math.PI) / 180;

    const offsetX = Math.cos(angleRad) * bubble.shadowDistance;
    const offsetY = Math.sin(angleRad) * bubble.shadowDistance;

    // Convert alpha to hex
    const alphaHex = Math.round(bubble.shadowAlpha * 255)
      .toString(16)
      .padStart(2, '0');

    return `${offsetX}px ${offsetY}px ${bubble.shadowSize}px ${bubble.shadowColor}${alphaHex}`;
  };

  const showBubble = (bubbleData, displayTime) => {
    const bubbleStyle = {
      position: 'fixed',
      top: `${bubbleData.coordinateY}%`,
      left: `${bubbleData.coordinateX}%`,
      transform: 'translate(-50%, -50%)',
      zIndex: 9999,
      pointerEvents: 'none',
    };

    if ((bubbleData.custom === true || bubbleData.method === 'custom')) {
      bubbleStyle.width = `${pxToVw(bubbleData?.width)}`;
    } else {
      bubbleStyle.backgroundColor = hexToRgba(bubbleData.bubbleColor, bubbleData.opacity);
      bubbleStyle.border = `2px solid ${bubbleData.outlineColor}`;
      bubbleStyle.fontFamily = bubbleData.fontStyle;
      bubbleStyle.fontSize = `${pxToVw(bubbleData.fontSize)}`;
      bubbleStyle.color = bubbleData.fontColor;
      bubbleStyle.boxShadow = getShadowCSS(bubbleData);
      bubbleStyle.padding = pxToVw(bubbleData.padding);
      bubbleStyle.borderRadius = pxToVw(bubbleData.cornerRadius);
      bubbleStyle.maxWidth = '100vw';
      bubbleStyle.textAlign = 'center';
      bubbleStyle.width = `${pxToVw(bubbleData?.width)}`;
    }

    const id = `${bubbleData._id}_${Date.now()}`;

    // Create bubble (with enter animation)
    const newBubble = {
      id,
      ...bubbleData,
      style: bubbleStyle,
      animation: 'bubble-enter',
    };

    setBubbles(prev => [...prev, newBubble]);

    // If displayTime = 0 → keep bubble forever
    if (displayTime === 0) return;

    // Start fade-out BEFORE removal
    setTimeout(
      () => {
        setBubbles(prev => prev.map(b => (b.id === id ? { ...b, animation: 'bubble-exit' } : b)));
      },
      (displayTime - 0.45) * 1000
    ); // fade starts before end

    // Actually remove bubble
    setTimeout(() => {
      setBubbles(prev => prev.filter(b => b.id !== id));
    }, displayTime * 1000);
  };

  // Inject animation CSS once
  const injectBubbleAnimations = () => {
    if (document.getElementById('bubble-animations')) return;

    const style = document.createElement('style');
    style.id = 'bubble-animations';
    style.innerHTML = `
       @keyframes bubbleEnter {
      0% { opacity: 0; transform: scale(0.7) translate(-50%, -50%); }
      60% { opacity: 1; transform: scale(1.05) translate(-50%, -50%); }
      100% { opacity: 1; transform: scale(1) translate(-50%, -50%); }
    }
    
    @keyframes bubbleExit {
      0% { opacity: 1; transform: scale(1) translate(-50%, -50%); }
      100% { opacity: 0; transform: scale(0.7) translate(-50%, -50%); }
    }
    
    .bubble-enter {
      animation: bubbleEnter 0.45s cubic-bezier(0.22, 1, 0.36, 1) forwards;
    }
    
    .bubble-exit {
      animation: bubbleExit 0.45s ease-in forwards;
    }
    
      `;
    document.head.appendChild(style);
  };


  useEffect(() => {
    const items = aiFormData?.bubble_manager || [];
    const bubbleTimeConfig = aiFormData?.bubble_time_config;
    if (!items.length) return;

    const initialDelay = bubbleTimeConfig?.initial_delay ?? 0;
    const displayTime = bubbleTimeConfig?.bubble_time ?? 0;
    const interval = bubbleTimeConfig?.interval ?? 0;

    let index = 0; // <-- WHICH BUBBLE TO SHOW
    let startTimeout = null;
    let loopInterval = null;

    const showNextBubble = () => {
      const bubble = items[index];
      showBubble(bubble, displayTime);

      // Move to next bubble
      index = (index + 1) % items.length; // loops forever
    };

    // If displayTime = 0 → bubble stays forever → show only once
    if (displayTime === 0) {
      setTimeout(() => showNextBubble(), initialDelay * 1000);
      return () => { };
    }

    // Start after initial delay
    startTimeout = setTimeout(() => {
      showNextBubble();

      // If only 1 bubble → this still loops fine
      loopInterval = setInterval(
        () => showNextBubble(),
        (displayTime + interval) * 1000
      );

    }, initialDelay * 1000);

    return () => {
      clearTimeout(startTimeout);
      clearInterval(loopInterval);
    };
  }, [aiFormData]);



  // useEffect(() => {
  //   const items = aiFormData?.bubble_manager || [];
  //   const bubbleTimeConfig = aiFormData?.bubble_time_config;
  //   if (!items.length) return;

  //   const initialDelay = bubbleTimeConfig?.initial_delay ?? 0;
  //   const displayTime = bubbleTimeConfig?.bubble_time ?? 0;
  //   const interval = bubbleTimeConfig?.interval ?? 0;

  //   let index = 0;
  //   let startTimeout = null;
  //   let loopInterval = null;

  //   const showNextBubble = () => {
  //     const bubble = items[index];
  //     showBubble(bubble, displayTime);
  //     index = (index + 1) % items.length;
  //   };

  //   // --------------------------------------------
  //   // CASE: SHOW FOREVER  
  //   // if 1 bubble OR displayTime=0 OR interval=0
  //   // --------------------------------------------
  //   if (items.length === 1 || displayTime === 0 || interval === 0) {
  //     startTimeout = setTimeout(() => {
  //       showNextBubble();  // show only once
  //     }, initialDelay * 1000);

  //     return () => {
  //       clearTimeout(startTimeout);
  //     };
  //   }

  //   // --------------------------------------------
  //   // CASE: NORMAL MULTI-BUBBLE LOOPING
  //   // --------------------------------------------
  //   startTimeout = setTimeout(() => {
  //     showNextBubble();

  //     loopInterval = setInterval(
  //       () => showNextBubble(),
  //       (displayTime + interval) * 1000
  //     );
  //   }, initialDelay * 1000);

  //   return () => {
  //     clearTimeout(startTimeout);
  //     clearInterval(loopInterval);
  //   };
  // }, [aiFormData]);



  return (
    <div className="bubble-container" style={{ display: disableSpeechButton ? 'none' : '' }}>
      {bubbles.map((b) => (
        <div key={b.id} style={b.style} className={b.animation}
        >
          {(b.custom === true) || (b.method === 'custom') ? (
            <img
              src={b.bubbleImageUrl}
              alt="bubble-img"
              style={{ width: "100%", height: "auto" }}
            />
          ) : (
            <span>{b.message}</span>
          )}
        </div>
      ))}
    </div>
  )
}


export default BubblesIntegration;
